import { TZDateMini } from '@date-fns/tz';
import { endOfDay, startOfDay } from 'date-fns';

import {
  getRegionalFormat,
  getTimezone,
} from '@dynatrace-sdk/user-preferences';

import type { UndoSnapshot } from './types.js';
import type { TimeValue } from '../../core/types/time.js';
import { getDayPeriodValues } from '../forms-core/time/helpers.js';
import type { SpinButtonHandler } from '../spin-buttons/spin-button-group/types.js';

// Matches strict ISO 8601 date-only: YYYY-MM-DD or YYYY-MM-DDZ (with optional trailing Z/z)
// Examples https://regex101.com/r/Jz81ww/1
const DATE_ONLY_REGEX = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])[Zz]?$/;
// Matches common time-only strings: HH:MM, HH:MM:SS, HH:MM:SS.mmm — all with optional Z or ±hh:mm offset
// Examples: https://regex101.com/r/bSW29n/1
const TIME_ONLY_REGEX =
  /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d)(?:\.(\d{1,3}))?)?([Zz]|[+-](?:[01]\d|2[0-3]):[0-5]\d)?$/;

// Matches a full ISO 8601 datetime: YYYY-MM-DDT… with optional timezone.
// Used to detect ISO strings that need case normalisation or a missing TZ suffix.
// Validates value ranges: month 01–12, day 01–31, hour 00–23, minute/second 00–59.
// Examples: https://regex101.com/r/OI3HwW/1
const FULL_ISO_DATETIME_REGEX =
  /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])[Tt]([01]\d|2[0-3])(?::([0-5]\d)(?::([0-5]\d)(?:\.(\d{1,3}))?)?)?([Zz]|[+-]\d{2}:?\d{2})?$/;

/**
 * Normalises a pasted ISO-like string so that downstream parsers can handle it:
 * - Uppercases the `T` separator and any trailing `z` / `Z` in the timezone.
 * - When a full ISO datetime has no timezone suffix, interprets it as local time
 *   in the user's configured timezone (per ISO 8601) and converts to UTC.
 * Non-ISO input is returned unchanged.
 * @internal
 */
export function normalizeISOInput(input: string): string {
  const match = FULL_ISO_DATETIME_REGEX.exec(input);
  if (!match) {
    return input;
  }

  // Uppercase the T separator and any trailing z
  // The regex finds ISO timestamps with lowercase t and uppercase T
  // Examples: https://regex101.com/r/HMInC0/1
  let normalized = input.replace(/^(\d{4}-\d{2}-\d{2})[Tt]/, '$1T');

  const tzSuffix = match[8];
  if (tzSuffix) {
    // Uppercase a lowercase 'z' at the end
    if (tzSuffix === 'z') {
      normalized = normalized.slice(0, -1) + 'Z';
    }
  } else {
    // No timezone suffix — per ISO 8601 this means local time.
    // Interpret in the user's configured timezone and convert to UTC.
    const year = Number(match[1]);
    const month = Number(match[2]) - 1; // 0-indexed
    const day = Number(match[3]);
    const hours = Number(match[4]);
    const minutes = match[5] !== undefined ? Number(match[5]) : 0;
    const seconds = match[6] !== undefined ? Number(match[6]) : 0;
    const milliseconds =
      match[7] !== undefined ? Number(match[7].padEnd(3, '0')) : 0;

    const local = TZDateMini.tz(
      getTimezone(),
      year,
      month,
      day,
      hours,
      minutes,
      seconds,
      milliseconds,
    );

    if (!isNaN(local.getTime())) {
      // Guard against impossible dates like Feb 31 — TZDateMini rolls them
      // over to the next month, so if the resulting date doesn't match the
      // input, return the input unchanged (not a valid ISO datetime).
      if (local.getMonth() !== month || local.getDate() !== day) {
        return input;
      }
      return local.toISOString();
    }

    // Fallback: append Z if the date is somehow invalid for the timezone
    normalized += 'Z';
  }

  return normalized;
}

/**
 * Parses a time-only string to a full ISO UTC datetime string anchored at `2000-01-01`.
 * Accepts: `HH:MM`, `HH:MMZ`, `HH:MM:SS`, `HH:MM:SS.mmm`, with optional `Z` / `±hh:mm` offset.
 * Returns `null` if the input does not represent a valid time.
 * @internal
 */
export function parseTimeOnlyToISO(input: string): string | null {
  const match = TIME_ONLY_REGEX.exec(input);
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = match[3] !== undefined ? Number(match[3]) : 0;
  const milliseconds =
    match[4] !== undefined ? Number(match[4].padEnd(3, '0')) : 0;
  const timezoneSuffix = match[5];

  const now = TZDateMini.tz(getTimezone());
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed
  const day = now.getDate();

  if (timezoneSuffix) {
    // Explicit offset (Z, +02:00, …): build the full ISO string with that offset
    // and let the Date parser convert it to UTC correctly.

    const ISOdate = `${year}-${String(month + 1).padStart(2, '0')}-${String(
      day,
    ).padStart(2, '0')}`;
    const ISOtime = `T${match[1]}:${match[2]}:${String(seconds).padStart(
      2,
      '0',
    )}.${String(milliseconds).padStart(3, '0')}${timezoneSuffix.toUpperCase()}`;
    const fullISO = ISOdate + ISOtime;
    const parsedDate = new Date(fullISO);

    return isNaN(parsedDate.getTime()) ? null : parsedDate.toISOString();
  }

  // No TZ suffix: the user means local time — interpret it in the user's timezone
  // and convert to UTC via .toISOString().
  const local = TZDateMini.tz(
    getTimezone(),
    year,
    month,
    day,
    hours,
    minutes,
    seconds,
    milliseconds,
  );
  return isNaN(local.getTime()) ? null : local.toISOString();
}

/**
 * Parses a date-only string matched against the strict ISO 8601 date format (`YYYY-MM-DD`).
 * Returns a full ISO UTC midnight datetime string, or `null` if the input is not a valid date.
 * @internal
 */
export function parseDateOnlyToISO(
  input: string,
  anchor: 'startOfDay' | 'endOfDay' = 'startOfDay',
): string | null {
  const match = DATE_ONLY_REGEX.exec(input);
  if (!match) {
    return null;
  }

  const date = TZDateMini.tz(
    getTimezone(),
    Number(match[1]),
    Number(match[2]) - 1, // 0-indexed month
    Number(match[3]),
  );

  // Guard against impossible dates like Feb 31 — TZDateMini rolls them over to
  // the next month, so if the resulting month doesn't match the input, reject it.
  if (date.getMonth() !== Number(match[2]) - 1) {
    return null;
  }

  const anchored = anchor === 'endOfDay' ? endOfDay(date) : startOfDay(date);
  return anchored.toISOString();
}

/**
 * Captures the current state of a DateTimeInput's spinbuttons and expression input.
 * @internal
 */
export const captureUndoSnapshot = (
  spinButtonHandlers: Map<
    keyof Intl.DateTimeFormatPartTypesRegistry,
    SpinButtonHandler | null | undefined
  >,
  expressionRef: HTMLSpanElement | null | undefined,
  value: TimeValue | null,
  valueType: 'expression' | 'iso8601',
): UndoSnapshot => {
  const spinButtonTexts = new Map<Intl.DateTimeFormatPart['type'], string>();
  spinButtonHandlers.forEach((handler, type) => {
    spinButtonTexts.set(type, handler?.element.current?.textContent ?? '');
  });
  return {
    value,
    valueType,
    spinButtonTexts,
    expressionText: expressionRef?.textContent ?? '',
  };
};

type DateFieldType = 'day' | 'month' | 'year';

function isDateFieldPart(
  part: Intl.DateTimeFormatPart,
): part is Intl.DateTimeFormatPart & { type: DateFieldType } {
  return part.type === 'day' || part.type === 'month' || part.type === 'year';
}

/**
 * Returns the locale-specific date delimiter and field order (e.g. DMY, MDY, YMD)
 * by inspecting `Intl.DateTimeFormat.formatToParts`.
 */
function getRegionalDateFormat(): {
  delimiter: string;
  fieldOrder: [DateFieldType, DateFieldType, DateFieldType];
} {
  const parts = new Intl.DateTimeFormat(getRegionalFormat(), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const delimiter = parts.find((p) => p.type === 'literal')?.value ?? '/';
  const fieldOrder = parts.filter(isDateFieldPart).map((p) => p.type) as [
    DateFieldType,
    DateFieldType,
    DateFieldType,
  ];

  return { delimiter, fieldOrder };
}

export type PartialDate = Partial<Record<DateFieldType, number>>;

/**
 * Extracts a partial date from an input string using the user's regional date format.
 * Returns a `PartialDate` with `day`, `month`, and/or `year` fields mapped according
 * to the locale's field order. Handles incomplete inputs like `"12/"` or `"12.2024"`.
 * Returns `null` if no date pattern is found.
 * @internal
 */
export function tryParsePartialDate(input: string): PartialDate | null {
  const { delimiter, fieldOrder } = getRegionalDateFormat();
  // Escape the delimiter for use in the regex pattern
  // Examples: https://regex101.com/r/KjhFqd/1
  const escapedDelimiter = delimiter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Match two or three groups of digits separated by the locale's date delimiter.
  // The middle group allows 1-4 digits because with only 2 groups it may be a year
  // (e.g. "12.2024"). When all 3 groups are present the field-level validation
  // below rejects a middle value that is too long for its assigned field.
  // Examples: https://regex101.com/r/h0AFRV/1
  const regex = new RegExp(
    `(?<=^|[\\s,])(\\d{1,4})${escapedDelimiter}(\\d{1,4})${escapedDelimiter}?(\\d{1,4})?(?=[\\s,]|$)`,
  );

  const match = regex.exec(input);
  if (!match) {
    return null;
  }

  const rawValues = [match[1], match[2], match[3]].filter(
    (value): value is string => value !== undefined,
  );

  // If a 4-digit number appears in a position that the locale doesn't assign to
  // 'year', the user likely omitted leading fields (e.g. "12.2024" in DMY means
  // month.year, not day.month). Right-align the values to the field order so the
  // 4-digit value lands on the year slot.
  const hasDisplacedYear = rawValues.some(
    (value, index) => value.length === 4 && fieldOrder[index] !== 'year',
  );
  const isYearFirstLocale = fieldOrder[0] === 'year';
  const hasNoExplicitYear = !rawValues.some((value) => value.length === 4);
  // In these cases, we assume the user omitted leading fields and right-align the values.
  const fields =
    hasDisplacedYear || (isYearFirstLocale && hasNoExplicitYear)
      ? fieldOrder.slice(fieldOrder.length - rawValues.length)
      : fieldOrder.slice(0, rawValues.length);

  const maxDigits: Record<DateFieldType, number> = {
    year: 4,
    day: 2,
    month: 2,
  };
  const result: PartialDate = {};

  for (let i = 0; i < fields.length; i++) {
    const raw = rawValues[i];
    const field = fields[i];

    // Day and month must be at most 2 digits; year must be exactly 4 digits.
    if (raw.length > maxDigits[field]) {
      return null;
    }

    const value = parseInt(raw);

    // Reject values outside valid ranges so that downstream code never sees
    // e.g. month 20 and wraps it into a different month via Date rollover.
    if (field === 'month' && (value < 1 || value > 12)) {
      return null;
    }
    if (field === 'day' && (value < 1 || value > 31)) {
      return null;
    }

    result[field] = value;
  }

  return result;
}

type TimeFieldType = 'hour' | 'minute' | 'second' | 'millisecond';

export type PartialTime = Partial<Record<TimeFieldType, number>> & {
  dayPeriod?: string;
};

/**
 * Returns whether the user's locale uses a 12-hour clock and the locale-specific
 * day period labels (e.g. AM/PM, a.m./p.m.).
 */
function getRegionalTimeFormat(): {
  is12Hour: boolean;
  dayPeriodValues: [string, string];
} {
  const parts = new Intl.DateTimeFormat(getRegionalFormat(), {
    hour: 'numeric',
    minute: '2-digit',
  }).formatToParts(new Date());

  const hasDayPeriod = parts.some((p) => p.type === 'dayPeriod');
  const dayPeriodValues = getDayPeriodValues() as [string, string];

  return { is12Hour: hasDayPeriod, dayPeriodValues };
}

/**
 * Extracts a partial time from an input string using the user's regional time format.
 * Returns a `PartialTime` with `hour`, `minute`, `second`, `millisecond`, and/or
 * `dayPeriod` fields. Requires two-digit hours, minutes, and seconds.
 * For 12-hour locales, optionally matches the locale-specific AM/PM label.
 * Returns `null` if no time pattern is found.
 * @internal
 */
export function tryParsePartialTime(input: string): PartialTime | null {
  const { is12Hour, dayPeriodValues } = getRegionalTimeFormat();

  // Build day period matching: escape for regex and match case-insensitively.
  // Examples: https://regex101.com/r/KjhFqd/1
  const escapedPeriods = dayPeriodValues.map((value) =>
    value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
  );
  const dayPeriodPattern = is12Hour
    ? `(?:\\s*(${escapedPeriods.join('|')}))?`
    : '';

  // Match: hours : minutes : seconds? . milliseconds? dayPeriod? with appropriate
  // lookarounds to ensure we capture standalone time patterns in the input.
  // Minutes are required to avoid matching bare two-digit numbers as times.
  // Examples: https://regex101.com/r/MTnOAG/2
  const regex = new RegExp(
    `(?<=^|[\\s,])((?:[01]?\\d|2[0-3])):([0-5]\\d)(?::([0-5]\\d)(?:[.](\\d{1,3}))?)?${dayPeriodPattern}(?=[\\s,]|$)`,
    'i',
  );

  const match = regex.exec(input);
  if (match) {
    const result: PartialTime = {};

    result.hour = parseInt(match[1]);
    result.minute = parseInt(match[2]);
    result.second = match[3] !== undefined ? parseInt(match[3]) : undefined;
    result.millisecond =
      match[4] !== undefined ? parseInt(match[4]) : undefined;

    if (is12Hour && match[5] !== undefined) {
      // Normalize to the canonical day period value (preserve locale casing)
      const matched = match[5].toLowerCase();
      result.dayPeriod = dayPeriodValues.find(
        (p) => p.toLowerCase() === matched,
      );
    }

    return result;
  }

  // Secondary: mm:ss.f{1,3} — fractional part is required to disambiguate from hh:mm.
  // Examples: https://regex101.com/r/hoRKbs/1
  const millisecondsRegex =
    /(?<=^|[\s,])([0-5]\d):([0-5]\d)\.(\d{1,3})(?=[\s,]|$)/;
  const millisecondsMatch = millisecondsRegex.exec(input);
  if (millisecondsMatch) {
    return {
      minute: parseInt(millisecondsMatch[1]),
      second: parseInt(millisecondsMatch[2]),
      millisecond: parseInt(millisecondsMatch[3]),
    };
  }

  return null;
}

export type PartialDateTimeResult = {
  /** Normalized ISO string that can be applied to spin buttons. */
  iso: string;
  /**
   * If set, only these spin button parts should be updated (partial result).
   * When absent the result is complete and can be fed through the existing
   * paste pipeline (date-only / time-only / full ISO).
   */
  partsToUpdate?: Set<keyof Intl.DateTimeFormatPartTypesRegistry>;
};

/** Converts a 12-hour clock value to 24-hour using the locale's day-period labels. */
function convertTo24Hour(hour: number, dayPeriod: string): number {
  const { dayPeriodValues } = getRegionalTimeFormat();
  const isPM = dayPeriod.toLowerCase() === dayPeriodValues[1].toLowerCase();
  const isAM = dayPeriod.toLowerCase() === dayPeriodValues[0].toLowerCase();
  if (isPM && hour < 12) {
    return hour + 12;
  }
  if (isAM && hour === 12) {
    return 0;
  }
  return hour;
}

/** Builds a complete ISO datetime from fully-specified date and time fields. */
function buildFullDateTimeISO(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number | undefined,
  millisecond: number | undefined,
): PartialDateTimeResult | null {
  const d = TZDateMini.tz(
    getTimezone(),
    year,
    month - 1,
    day,
    hour,
    minute,
    second ?? 0,
    millisecond ?? 0,
  );
  return isNaN(d.getTime()) ? null : { iso: d.toISOString() };
}

/** Builds a date-only ISO string (YYYY-MM-DD) after validating the date. */
function buildDateOnlyISO(
  year: number,
  month: number,
  day: number,
): PartialDateTimeResult | null {
  const validationDate = TZDateMini.tz(getTimezone(), year, month - 1, day);
  if (
    isNaN(validationDate.getTime()) ||
    validationDate.getFullYear() !== year ||
    validationDate.getMonth() !== month - 1 ||
    validationDate.getDate() !== day
  ) {
    return null;
  }
  const yearString = String(year).padStart(4, '0');
  const monthString = String(month).padStart(2, '0');
  const dayString = String(day).padStart(2, '0');
  return { iso: `${yearString}-${monthString}-${dayString}` };
}

/** Builds a time-only ISO string (HH:MM[:SS[.mmm]]). */
function buildTimeOnlyISO(
  hour: number,
  minute: number,
  second: number | undefined,
  millisecond: number | undefined,
): PartialDateTimeResult {
  const hourString = String(hour).padStart(2, '0');
  const minuteString = String(minute).padStart(2, '0');
  let isoTime = `${hourString}:${minuteString}`;
  if (second !== undefined) {
    const secondString = String(second).padStart(2, '0');
    isoTime += `:${secondString}`;
    if (millisecond !== undefined) {
      const millisecondString = String(millisecond).padStart(3, '0');
      isoTime += `.${millisecondString}`;
    }
  }
  return { iso: isoTime };
}

/** Collects the set of spin-button part types that have defined values. */
function collectPartsToUpdate(
  date: PartialDate | null,
  time: PartialTime | null,
): Set<keyof Intl.DateTimeFormatPartTypesRegistry> {
  const partsToUpdate = new Set<keyof Intl.DateTimeFormatPartTypesRegistry>();

  const fieldMapping: Array<
    [unknown, keyof Intl.DateTimeFormatPartTypesRegistry]
  > = [
    [date?.day, 'day'],
    [date?.month, 'month'],
    [date?.year, 'year'],
    [time?.hour, 'hour'],
    [time?.minute, 'minute'],
    [time?.second, 'second'],
    [time?.millisecond, 'fractionalSecond'],
    [time?.dayPeriod, 'dayPeriod'],
  ];

  for (const [value, part] of fieldMapping) {
    if (value !== undefined) {
      partsToUpdate.add(part);
    }
  }

  return partsToUpdate;
}

/**
 * Converts partial date and/or time objects into an ISO-compatible string.
 *
 * - Complete date + complete time → full ISO datetime (no `partsToUpdate`).
 * - Complete date only → `"YYYY-MM-DD"` (no `partsToUpdate`).
 * - Complete time only → `"HH:MM[:SS[.mmm]]"` (no `partsToUpdate`).
 * - Partial fields → ISO string with defaults filled in, plus a `partsToUpdate`
 *   set that lists only the spin button parts that should actually be applied.
 * - No fields at all → `null`.
 *
 * A date is "complete" when it has `day`, `month`, and `year`.
 * A time is "complete" when it has `hour` and `minute`.
 * @internal
 */
export function partialDateTimeToISO(
  date: PartialDate | null,
  time: PartialTime | null,
): PartialDateTimeResult | null {
  if (!date && !time) {
    return null;
  }

  const year = date?.year;
  const month = date?.month;
  const day = date?.day;
  const minute = time?.minute;
  const second = time?.second;
  const millisecond = time?.millisecond;

  let hour24 = time?.hour;
  if (hour24 !== undefined && time?.dayPeriod) {
    hour24 = convertTo24Hour(hour24, time.dayPeriod);
  }

  if (year !== undefined && month !== undefined && day !== undefined) {
    if (hour24 !== undefined && minute !== undefined) {
      return buildFullDateTimeISO(
        year,
        month,
        day,
        hour24,
        minute,
        second,
        millisecond,
      );
    }
    return buildDateOnlyISO(year, month, day);
  }

  if (!date && hour24 !== undefined && minute !== undefined) {
    return buildTimeOnlyISO(hour24, minute, second, millisecond);
  }

  // Partial result: fill defaults from "now" for missing fields.
  const now = TZDateMini.tz(getTimezone());
  const fallback = TZDateMini.tz(
    getTimezone(),
    year ?? now.getFullYear(),
    (month ?? now.getMonth() + 1) - 1,
    day ?? now.getDate(),
    hour24 ?? now.getHours(),
    minute ?? now.getMinutes(),
    second ?? 0,
    millisecond ?? 0,
  );
  if (isNaN(fallback.getTime())) {
    return null;
  }

  const partsToUpdate = collectPartsToUpdate(date, time);
  if (partsToUpdate.size === 0) {
    return null;
  }

  return { iso: fallback.toISOString(), partsToUpdate };
}
